import { parseDate } from "@internationalized/date";
import { useDateFormatter } from "@react-aria/i18n";
import { useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import {
  CopyIcon,
  DeleteIcon,
  DollarSignIcon,
  EditIcon,
  EllipsisVerticalIcon,
  EyeIcon,
  PlusIcon,
  Trash2Icon,
  UserPlusIcon,
} from "lucide-react";
import { Suspense, useState } from "react";
import { Heading, Pressable } from "react-aria-components";
import type z from "zod";

import { CopyScheduleForm } from "@/components/admin/schedule/copy";
import { DeleteScheduleForm } from "@/components/admin/schedule/delete";
import { EnableInviteOnlyModal } from "@/components/admin/schedule/enable-invite-only-modal";
import { PublishScheduleForm } from "@/components/admin/schedule/publish";
import { Button } from "@/components/base/button";
import {
  DropdownMenu,
  DropdownMenuItem,
  DropdownMenuItemLink,
} from "@/components/base/dropdown-menu";
import { Modal } from "@/components/base/modal";
import { Pagination } from "@/components/base/pagination";
import { title } from "@/components/base/primitives";
import { Toolbar } from "@/components/base/toolbar";
import { DeleteTournamentForm } from "@/components/tournaments/controls/delete";
import { DuplicateForm } from "@/components/tournaments/controls/duplicate";
import { EditGeneralInfoForm } from "@/components/tournaments/controls/edit-general-info";
import { ManageInvitesForm } from "@/components/tournaments/controls/manage-invites";
import { ManagePricesForm } from "@/components/tournaments/controls/manage-prices";
import {
  TournamentListFilters,
  tournamentListFilterSchema,
} from "@/components/tournaments/filters";
import { DivisionsForm } from "@/components/tournaments/forms/divisions";
import { useTRPC } from "@/integrations/trpc/react";
import { getDefaultTimeZone } from "@/lib/dates";

export const bulkEditScheduleSearchSchema = tournamentListFilterSchema
  .omit({
    // past: true,
  })
  .extend({
    // past: z.boolean().default(false),
  });

export type BulkEditScheduleProps = {
  params: z.infer<typeof bulkEditScheduleSearchSchema>;
};

export function BulkEditSchedule(props: BulkEditScheduleProps) {
  const queryClient = useQueryClient();
  const trpc = useTRPC();
  const queryProps = trpc.tournaments.search.queryOptions(props.params);

  const { data: tournaments } = useSuspenseQuery(queryProps);

  const dateFormatter = useDateFormatter({ timeZone: getDefaultTimeZone() });

  const [editGeneralId, setEditGeneralId] = useState<number | undefined>();
  const [copyId, setCopyId] = useState<number | undefined>();
  const [deleteId, setDeleteId] = useState<number | undefined>();
  const [manageInvitesId, setManageInvitesId] = useState<number | undefined>();
  const [enableInviteOnlyId, setEnableInviteOnlyId] = useState<
    number | undefined
  >();
  const [managePricesId, setManagePricesId] = useState<number | undefined>();
  const [isCopyScheduleOpen, setIsCopyScheduleOpen] = useState(false);
  const [isDeleteScheduleOpen, setIsDeleteScheduleOpen] = useState(false);
  const [isPublishScheduleOpen, setIsPublishScheduleOpen] = useState(false);

  return (
    <div>
      <div className="bg-slate-300 py-8">
        <TournamentListFilters {...props.params} />
      </div>

      <div className="mx-auto flex max-w-lg flex-row justify-end pt-6 pb-2">
        <DropdownMenu
          label="Schedule actions"
          buttonIcon={<EllipsisVerticalIcon />}
        >
          <DropdownMenuItemLink to="/tournaments/create">
            <PlusIcon size={16} /> Create tournament
          </DropdownMenuItemLink>
          <DropdownMenuItem onAction={() => setIsCopyScheduleOpen(true)}>
            <CopyIcon size={16} /> Copy schedule
          </DropdownMenuItem>
          <DropdownMenuItem onAction={() => setIsPublishScheduleOpen(true)}>
            <EyeIcon size={16} /> Publish schedule
          </DropdownMenuItem>
          <DropdownMenuItem
            onAction={() => setIsDeleteScheduleOpen(true)}
            className="text-red-600"
          >
            <Trash2Icon size={16} /> Delete schedule
          </DropdownMenuItem>
        </DropdownMenu>
      </div>
      <Suspense fallback={<ScheduleListSkeleton />}>
        <div className="mx-auto flex max-w-lg flex-col space-y-4 pt-4 pb-12">
          {tournaments?.data.map(
            ({
              id,
              name,
              date,
              inviteOnly,
              venue: { name: venueName, city },
            }) => {
              const parsedDate = parseDate(date);

              return (
                <div key={id} className="relative">
                  <Toolbar
                    orientation="vertical"
                    className="space-y absolute top-2 -right-2 flex translate-x-full flex-col"
                  >
                    <Button
                      variant="icon"
                      onPress={() => {
                        setCopyId(id);
                      }}
                    >
                      <CopyIcon />
                    </Button>
                    <Button
                      className="text-red-500"
                      variant="icon"
                      onPress={() => {
                        setDeleteId(id);
                      }}
                    >
                      <DeleteIcon />
                    </Button>
                    <Button
                      variant="icon"
                      onPress={() => {
                        if (inviteOnly) {
                          setManageInvitesId(id);
                        } else {
                          setEnableInviteOnlyId(id);
                        }
                      }}
                    >
                      <UserPlusIcon />
                    </Button>
                    <Button
                      variant="icon"
                      onPress={() => {
                        setManagePricesId(id);
                      }}
                    >
                      <DollarSignIcon />
                    </Button>
                  </Toolbar>
                  <div className="overflow-hidden rounded-lg bg-white">
                    <Pressable
                      onPress={() => {
                        setEditGeneralId(id);
                      }}
                    >
                      <div className="bg-navbar-background text-navbar-foreground cursor-pointer p-2 hover:underline">
                        {name && (
                          <div className="flex flex-row items-center space-x-2">
                            <EditIcon size={18} /> <span>{name}</span>
                          </div>
                        )}
                        <div className="flex flex-row justify-between font-semibold">
                          <div className="flex flex-row items-center space-x-2">
                            {!name && <EditIcon size={18} />}
                            <span>
                              {venueName}, {city}
                            </span>
                          </div>
                          <div>
                            {dateFormatter.format(
                              parsedDate.toDate(getDefaultTimeZone()),
                            )}
                          </div>
                        </div>
                      </div>
                    </Pressable>
                    <div className="flex flex-col rounded bg-white p-2">
                      <DivisionsForm tournamentId={id} />
                    </div>
                  </div>
                  {editGeneralId === id && (
                    <EditGeneralInfoForm
                      tournamentId={id}
                      isOpen={editGeneralId === id}
                      onOpenChange={() => setEditGeneralId(undefined)}
                      onSuccess={() => {
                        queryClient.invalidateQueries(queryProps);
                        queryClient.invalidateQueries(
                          trpc.tournaments.pathFilter(),
                        );
                      }}
                    />
                  )}
                  {deleteId === id && (
                    <DeleteTournamentForm
                      tournamentId={id}
                      isOpen={deleteId === id}
                      onOpenChange={() => setDeleteId(undefined)}
                      onSuccess={() => {
                        queryClient.invalidateQueries(queryProps);
                      }}
                    />
                  )}

                  {copyId === id && (
                    <DuplicateForm
                      tournamentId={id}
                      isOpen={copyId === id}
                      onOpenChange={() => setCopyId(undefined)}
                      onSuccess={() => {
                        queryClient.invalidateQueries(queryProps);
                      }}
                    />
                  )}

                  {manageInvitesId === id && (
                    <ManageInvitesForm
                      tournamentId={id}
                      isOpen={manageInvitesId === id}
                      onOpenChange={() => setManageInvitesId(undefined)}
                    />
                  )}

                  {managePricesId === id && (
                    <ManagePricesForm
                      tournamentId={id}
                      isOpen={managePricesId === id}
                      onOpenChange={() => setManagePricesId(undefined)}
                    />
                  )}

                  {enableInviteOnlyId === id && (
                    <EnableInviteOnlyModal
                      tournamentId={id}
                      isOpen={enableInviteOnlyId === id}
                      onOpenChange={() => setEnableInviteOnlyId(undefined)}
                      onSuccess={() => {
                        setEnableInviteOnlyId(undefined);
                        setManageInvitesId(id);
                        queryClient.invalidateQueries(queryProps);
                        queryClient.invalidateQueries(
                          trpc.tournaments.pathFilter(),
                        );
                      }}
                    />
                  )}
                </div>
              );
            },
          )}
          <Pagination
            to="/admin/schedule"
            page={props.params.page}
            pageSize={props.params.pageSize}
            pageInfo={tournaments.pageInfo}
          />
        </div>
      </Suspense>

      <Modal
        isOpen={isCopyScheduleOpen}
        onOpenChange={setIsCopyScheduleOpen}
        isDismissable
      >
        <div className="flex max-w-md flex-col space-y-4 p-6">
          <Heading className={title({ size: "sm" })} slot="title">
            Copy Schedule
          </Heading>
          <CopyScheduleForm />
        </div>
      </Modal>

      <Modal
        isOpen={isDeleteScheduleOpen}
        onOpenChange={setIsDeleteScheduleOpen}
        isDismissable
      >
        <div className="flex max-w-md flex-col space-y-4 p-6">
          <Heading className={title({ size: "sm" })} slot="title">
            Delete Schedule
          </Heading>
          <DeleteScheduleForm />
        </div>
      </Modal>

      <Modal
        isOpen={isPublishScheduleOpen}
        onOpenChange={setIsPublishScheduleOpen}
        isDismissable
      >
        <div className="flex max-w-md flex-col space-y-4 p-6">
          <Heading className={title({ size: "sm" })} slot="title">
            Publish Schedule
          </Heading>
          <PublishScheduleForm
            onSuccess={() => setIsPublishScheduleOpen(false)}
          />
        </div>
      </Modal>
    </div>
  );
}

function SkeletonDivisionRow() {
  return (
    <div className="flex flex-row items-center justify-between py-2">
      <div className="h-4 w-48 animate-pulse rounded bg-gray-200" />
      <div className="flex flex-row gap-2">
        <div className="h-7 w-14 animate-pulse rounded bg-gray-200" />
        <div className="h-7 w-7 animate-pulse rounded bg-gray-200" />
      </div>
    </div>
  );
}

function ScheduleItemSkeleton() {
  return (
    <div className="overflow-hidden rounded-lg bg-white">
      <div className="bg-navbar-background p-2">
        <div className="flex flex-row justify-between">
          <div className="h-5 w-40 animate-pulse rounded bg-white/20" />
          <div className="h-5 w-24 animate-pulse rounded bg-white/20" />
        </div>
      </div>
      <div className="flex flex-col divide-y divide-gray-300 p-2">
        <SkeletonDivisionRow />
        <SkeletonDivisionRow />
      </div>
    </div>
  );
}

function ScheduleListSkeleton() {
  return (
    <div className="mx-auto flex max-w-lg flex-col space-y-4 pt-4 pb-12">
      <ScheduleItemSkeleton />
      <ScheduleItemSkeleton />
      <ScheduleItemSkeleton />
      <ScheduleItemSkeleton />
    </div>
  );
}
